/** My profile - you in this workspace, and how you sign in (card C-A, gate 30; ADR-0212: reached
 * from the avatar menu, its own page).
 *
 * **Everything on it is about the caller.** The routes take no person's id, so nothing on this
 * screen can be pointed at anyone else, and there is no role check here for a reason: every role has
 * a name, an email, a role and a profile, an Auditor included. Not gating is the finding, not an
 * omission - the opposite (a control shown to a role the server refuses) is UX-04.
 *
 * **Three states for a read, and no fourth.** Loading, failed and loaded (`Loaded`). A failed read is
 * a failure with a way to try again, never an empty profile: a form that rendered blank on a dropped
 * connection would invite a person to type their name into a page that has forgotten it.
 *
 * **What is real and what is not.** The name, the language and the time zone are served and saved
 * (`PATCH /profile`). The password, the passkeys and the live sessions are served and acted on
 * (`ProfileSecurity.tsx`, `PasskeySettings.tsx`), and so is the person's own sign-in history
 * (`GET /profile/sign-ins`, G-P8-07). **A photo, a job title, a phone number and new recovery codes
 * have no endpoint**, so each is a row that says "Not available yet" and not a control that would
 * only be saved or only fail. The second factor is shown, not managed
 * here: enrolment is an identity flow of its own (ADR-0008), and a role that must have one is told so
 * behind the info control. Units are not a setting (gate 30; ADR-0210) and are not on this page; the
 * explanation is on My settings, where a person would look for them.
 *
 * Every explanation that stood on this page as a paragraph is in `infoText.ts`, section `account`.
 */

import { useMemo, useState } from "react";
import type { FormEvent } from "react";

import { Refused } from "../api/refusal";
import { LOCALES, filterZoneChoices, takePreferencesSaved, useProfile, useUpdateProfile, zoneChoices } from "../api/profile";
import type { Profile } from "../api/profile";
import { FieldError } from "../components/FieldError";
import { Loaded, WriteFailed } from "../components/Loaded";
import { useFocusRefusal } from "../components/useFocusRefusal";
import { useFormChecks } from "../components/useFormChecks";
import { StatusPill } from "../components/StatusPill";
import { StatusChip } from "../components/ui";
import { UserCircle } from "@phosphor-icons/react";
import { usePasskeys } from "../api/passkeys";
import { PasskeyPanelBody } from "./PasskeySettings";
import { PasswordPanel, SessionsPanel, SignInHistoryPanel } from "./ProfileSecurity";
import { NotYet, SettingsPage, SettingsRow, SettingsSection } from "./SettingsPage";
import { RequireSession } from "../shell/RequireSession";
import own from "./Profile.module.css";
import { ScreenFrame } from "./ScreenFrame";
import styles from "./Screen.module.css";
import { useScreen } from "./useScreen";

const ROLE_LABEL: Record<string, string> = {
  admin: "Admin",
  approver: "Approver",
  contributor: "Contributor",
  viewer: "Viewer",
  auditor: "Auditor",
};

/** The roles for which a second factor is a rule rather than a choice (`identity.service`'s
 * `MFA_REQUIRED_ROLES`, ADR-0008). Mirrored so the sentence that explains it appears beside the
 * state; the **server** enforces it, so a stale copy here can only fail to explain, never to
 * permit. */
const SECOND_FACTOR_REQUIRED: readonly string[] = ["admin", "approver"];

/** Up to two initials for the mark beside the name. Letters only, by code point, so a name that
 * starts with a digit or an emoji falls back to a neutral dot. Decoration (`aria-hidden`): the name
 * beside it is the text. */
function initials(name: string): string {
  const words = name.split(/\s+/).filter((word) => /^\p{L}/u.test(word));
  const first = words[0]?.match(/^\p{L}/u)?.[0] ?? "";
  const last = words.length > 1 ? (words[words.length - 1]?.match(/^\p{L}/u)?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "·";
}

/** The sentence for a refusal that is about a field, or `null` when it is not about one. */
function fieldRefusal(error: unknown, code: string): string | null {
  return error instanceof Refused && error.code === code ? error.message : null;
}

function IdentityPanel({ profile }: { profile: Profile }) {
  const update = useUpdateProfile();
  const [draft, setDraft] = useState(profile.display_name);
  const [saved, setSaved] = useState(false);
  const refusal = fieldRefusal(update.error, "invalid_display_name");
  const [checks, attach] = useFormChecks();
  // After the server refuses, focus goes to the name box it refused (G-P8-45), not the button.
  useFocusRefusal(checks.element, update.error);

  function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    // The server's own sentence for a blank name is "Your name cannot be empty."; it is said here
    // before the request so the button never has to be grey to say it (G-P8-03).
    const ok = checks.run([
      { field: "name", ok: draft.trim() !== "", message: "Your name cannot be empty." },
      {
        field: "name",
        ok: draft.trim() === "" || draft.trim() !== profile.display_name,
        message: "That is already your name. Change it, then save.",
      },
    ]);
    if (!ok) return;
    update.mutate(
      { displayName: draft },
      {
        onSuccess: (next) => {
          setDraft(next.display_name);
          setSaved(true);
        },
      },
    );
  }

  return (
    <>
      <div className={own.identity} data-testid="profile-identity">
        <span className={own.avatar} aria-hidden="true">
          {initials(profile.display_name)}
        </span>
        <span className={own.identityText}>
          <span className={own.identityName}>{profile.display_name}</span>
          <span className={own.identityEmail} data-testid="profile-email">
            {profile.email}
          </span>
        </span>
      </div>

      <dl className={own.facts} data-testid="profile-facts">
        <div>
          <dt>Role in this workspace</dt>
          <dd data-testid="profile-role">{ROLE_LABEL[profile.role] ?? profile.role}</dd>
        </div>
        <div>
          <dt>Second factor</dt>
          <dd data-testid="profile-mfa" data-enrolled={profile.mfa_enrolled ? "yes" : "no"}>
            {/* A state is a word and a shape: enrolled is a tick, not enrolled is a warning, and
                for a role that must have one the warning is the louder of the two. */}
            <StatusPill
              tone={
                profile.mfa_enrolled
                  ? "ok"
                  : SECOND_FACTOR_REQUIRED.includes(profile.role)
                    ? "warn"
                    : "neutral"
              }
            >
              {profile.mfa_enrolled ? "Enrolled" : "Not enrolled"}
            </StatusPill>
          </dd>
        </div>
      </dl>

      <form ref={attach} className={own.form} onSubmit={submit} noValidate data-testid="profile-name-form">
        <label className={own.field}>
          Display name
          <input
            type="text"
            disabled={update.isPending}
            value={draft}
            maxLength={200}
            autoComplete="name"
            {...checks.field("name")}
            aria-invalid={refusal !== null || checks.errors.name !== undefined ? true : undefined}
            data-testid="profile-name"
            onChange={(event) => {
              checks.touch("name");
              setDraft(event.target.value);
              setSaved(false);
            }}
          />
          <FieldError {...checks.say("name")} />
        </label>
        {refusal ? (
          <p className={own.refused} role="alert" data-testid="profile-name-refused">
            {refusal}
          </p>
        ) : (
          <WriteFailed error={update.error} of="your name" />
        )}
        {saved && !update.isPending ? (
          <p className={own.saved} role="status" data-testid="profile-name-saved">
            Saved.
          </p>
        ) : null}
        <span>
          <button
            type="submit"
            className={styles.primary}
            disabled={update.isPending}
            data-testid="profile-name-save"
          >
            {update.isPending ? "Saving…" : "Save name"}
          </button>
        </span>
      </form>
    </>
  );
}

function PreferencesPanel({ profile }: { profile: Profile }) {
  const update = useUpdateProfile();
  const [locale, setLocale] = useState<"en-GB" | "en-US">(
    profile.locale === "en-US" ? "en-US" : "en-GB",
  );
  const [zone, setZone] = useState(profile.time_zone);
  const [zoneQuery, setZoneQuery] = useState("");
  const [saved, setSaved] = useState(takePreferencesSaved);
  const everyZone = useMemo(() => zoneChoices(profile.time_zone), [profile.time_zone]);
  const zones = useMemo(() => filterZoneChoices(everyZone, zoneQuery, zone), [everyZone, zoneQuery, zone]);
  // How many the search matches, **not counting the zone kept in the list for being the chosen one**.
  const matches = useMemo(() => filterZoneChoices(everyZone, zoneQuery, "").length, [everyZone, zoneQuery]);
  const zoneRefusal = fieldRefusal(update.error, "invalid_time_zone");
  const changed = locale !== profile.locale || zone !== profile.time_zone;

  function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    update.mutate({ locale, timeZone: zone }, { onSuccess: () => setSaved(true) });
  }

  return (
    <form className={own.form} onSubmit={submit} data-testid="profile-preferences-form">
      <label className={own.field}>
        Language and time format
        <select
          value={locale}
          disabled={update.isPending}
          data-testid="profile-locale"
          onChange={(event) => {
            setLocale(event.target.value === "en-US" ? "en-US" : "en-GB");
            setSaved(false);
          }}
        >
          {LOCALES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <span data-testid="profile-locale-note" data-applied="yes">
          <StatusChip tone="ok" size="sm">
            Applied
          </StatusChip>
        </span>
      </label>
      {/* A search for the city (G-Wadmin-26): four hundred zones is a list nobody scrolls. It is its own
          control, outside the label of the select it filters (a label names one control), named for
          assistive technology and by its placeholder, so it adds no body text. */}
      <span className={own.field}>
        <input
          type="search"
          value={zoneQuery}
          placeholder="Search time zones by city or country"
          aria-label="Search time zones"
          autoComplete="off"
          disabled={update.isPending}
          data-testid="profile-time-zone-search"
          onChange={(event) => setZoneQuery(event.target.value)}
        />
      </span>
      <label className={own.field}>
        Time zone
        <select
          value={zone}
          disabled={update.isPending}
          aria-invalid={zoneRefusal !== null}
          data-testid="profile-time-zone"
          onChange={(event) => {
            setZone(event.target.value);
            setSaved(false);
          }}
        >
          {zones.map((choice) => (
            <option key={choice.value} value={choice.value}>
              {choice.label}
            </option>
          ))}
        </select>
        {zoneQuery.trim() !== "" ? (
          <span role="status" data-testid="profile-time-zone-count">
            {matches === 0
              ? "No time zone matches."
              : `${String(matches)} time ${matches === 1 ? "zone" : "zones"}.`}
          </span>
        ) : null}
        <span data-testid="profile-time-zone-note" data-applied="yes">
          <StatusChip tone="ok" size="sm">
            Applied
          </StatusChip>
        </span>
      </label>
      {zoneRefusal ? (
        <p className={own.refused} role="alert" data-testid="profile-time-zone-refused">
          {zoneRefusal}
        </p>
      ) : (
        <WriteFailed error={update.error} of="your preferences" />
      )}
      {saved && !update.isPending ? (
        <p className={own.saved} role="status" data-testid="profile-preferences-saved">
          Saved.
        </p>
      ) : null}
      <span>
        <button
          type="submit"
          className={styles.primary}
          disabled={!changed || update.isPending}
          data-testid="profile-preferences-save"
        >
          {update.isPending ? "Saving…" : "Save preferences"}
        </button>
      </span>
    </form>
  );
}

function ProfileScreenBody() {
  const screen = useScreen();
  const profile = useProfile();
  const passkeys = usePasskeys();

  return (
    <ScreenFrame screen={screen} title="My profile" crumb="you, in this workspace">
      <SettingsPage icon={UserCircle} title="My profile" label="You, in this workspace">
        <SettingsSection title="You" infoKey="account.profile" testId="profile-you">
          <Loaded query={profile} of="Your profile">
            {/* Not keyed on the saved value: a remount would discard the "Saved." that confirms it.
                The draft is replaced with the server's answer in `onSuccess` instead. */}
            {(value) => <IdentityPanel profile={value} />}
          </Loaded>
          <NotYet label="Photo" testId="profile-photo-unavailable" />
          <NotYet label="Job title" testId="profile-title-unavailable" />
          <NotYet label="Phone" testId="profile-phone-unavailable" />
        </SettingsSection>
        <SettingsSection title="Time zone and language" infoKey="account.preferences" testId="profile-preferences">
          <Loaded query={profile} of="Your preferences">
            {(value) => <PreferencesPanel profile={value} />}
          </Loaded>
        </SettingsSection>
        <SettingsSection title="Password" infoKey="account.password" testId="profile-password">
          <PasswordPanel />
        </SettingsSection>
        <SettingsSection title="Two-step sign-in" infoKey="account.two-step" testId="profile-two-step">
          <NotYet label="New recovery codes" testId="profile-recovery-unavailable" />
          <SettingsRow label="Passkeys">
            <span data-testid="profile-passkey-count">{passkeys.data ? String(passkeys.data.length) : "-"}</span>
          </SettingsRow>
          <PasskeyPanelBody />
        </SettingsSection>
        <SettingsSection title="Active sessions" infoKey="account.sessions" testId="profile-sessions">
          <SessionsPanel />
        </SettingsSection>
        <SettingsSection title="Sign-in history" testId="profile-login-history">
          <SignInHistoryPanel />
        </SettingsSection>
      </SettingsPage>
    </ScreenFrame>
  );
}

export function ProfileScreen() {
  return (
    <RequireSession>
      <ProfileScreenBody />
    </RequireSession>
  );
}
